import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, ChevronDown, LogOut, Settings, Plus, Check, User } from "lucide-react";
import { useAuth, api } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Sidebar Instagram Identity module.
 *
 * Renders the Instagram-first identity card at the bottom of the
 * sidebar, with three distinct states per the product spec §2:
 *   A — no connected Instagram account ("Connect Instagram" CTA)
 *   B — exactly one connected account (DP + @username + actions)
 *   C — multiple connected accounts (active shown + switcher)
 *
 * DP + @username come from `GET /api/instagram/accounts` — the backend
 * is the source of truth. Any change (connect/disconnect/switch) fires
 * an `ig-accounts-changed` window event so the component re-fetches
 * without the parent having to thread props everywhere.
 */
export default function SidebarInstagramIdentity() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [data, setData] = useState(null); // { accounts, active_account_id, plan_limit, can_add_another }
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const refetch = useCallback(async () => {
    try {
      const r = await api.get("/instagram/accounts");
      setData(r.data);
    } catch {
      setData({ accounts: [], active_account_id: null, plan_limit: 1, can_add_another: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
    const onChange = () => refetch();
    window.addEventListener("ig-accounts-changed", onChange);
    return () => window.removeEventListener("ig-accounts-changed", onChange);
  }, [refetch]);

  const connected = (data?.accounts || []).filter((a) => a.is_connected);
  const active = connected.find((a) => a.id === data?.active_account_id) || connected[0] || null;
  const multi = connected.length > 1;

  const handleLogout = () => { logout(); navigate("/"); };
  const openManage = () => navigate("/instagram/accounts");
  const openConnect = () => navigate("/connect-instagram");

  const handleSwitch = async (accId) => {
    if (switching || accId === active?.id) return;
    setSwitching(true);
    try {
      await api.post(`/instagram/accounts/${accId}/activate`);
      window.dispatchEvent(new CustomEvent("ig-accounts-changed"));
      // Tell account-scoped pages to drop stale data. They can listen
      // for this same event and refetch.
      window.dispatchEvent(new CustomEvent("ig-active-account-switched", { detail: { accountId: accId } }));
    } finally {
      setSwitching(false);
    }
  };

  if (loading) {
    return (
      <div className="px-3 py-2 flex items-center gap-3" data-testid="sidebar-ig-loading">
        <div className="w-10 h-10 rounded-full bg-secondary animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-secondary rounded animate-pulse" />
          <div className="h-2.5 w-2/3 bg-secondary rounded animate-pulse" />
        </div>
      </div>
    );
  }

  // ═══ STATE A: no Instagram connected ═══
  if (!active) {
    return (
      <div className="px-3 py-3" data-testid="sidebar-ig-state-empty">
        <button
          onClick={openConnect}
          className="w-full p-3 rounded-xl text-left bg-gradient-to-tr from-[#FFAE00]/10 via-[#FF0F7B]/10 to-[#682AE9]/10 border border-border/50 hover:border-primary/40 transition-all group"
          data-testid="sidebar-ig-connect-cta"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] flex items-center justify-center shrink-0">
              <Instagram className="w-[18px] h-[18px] text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">Connect Instagram</p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Connect your Instagram to unlock inbox, comments, automations, and analytics.
          </p>
        </button>
        <div className="mt-2 px-1">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </div>
    );
  }

  // ═══ STATES B + C: one or more accounts ═══
  return (
    <div className="px-2 py-2" data-testid={multi ? "sidebar-ig-state-multi" : "sidebar-ig-state-single"}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-secondary/50 transition-colors"
            data-testid="sidebar-ig-identity-trigger"
            aria-label="Instagram account menu"
          >
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] p-0.5 shrink-0">
              <div className="w-full h-full rounded-full bg-background overflow-hidden">
                {active.profile_picture_url ? (
                  <img
                    src={active.profile_picture_url}
                    alt={`@${active.username}`}
                    className="w-full h-full object-cover"
                    data-testid="sidebar-ig-dp"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
            {/* Flexible text container — `min-w-0` lets the inner text
                shrink past its intrinsic width so the username wraps to
                truncate-from-end instead of overflowing the sidebar.
                `title` gives the full @handle on hover (desktop tooltip). */}
            <div className="flex-1 min-w-0 text-left">
              <p
                className="text-sm font-medium truncate leading-tight"
                data-testid="sidebar-ig-username"
                title={`@${active.username}`}
              >
                @{active.username}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {multi ? `${connected.length} accounts connected` : user?.email}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" side="top" className="w-64" data-testid="sidebar-ig-menu">
          {multi && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Switch account
              </DropdownMenuLabel>
              {connected.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  onClick={() => handleSwitch(a.id)}
                  className="flex items-center gap-3 py-2 cursor-pointer"
                  data-testid={`sidebar-ig-switch-${a.id}`}
                >
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] p-0.5 shrink-0">
                    <div className="w-full h-full rounded-full bg-background overflow-hidden">
                      {a.profile_picture_url ? (
                        <img src={a.profile_picture_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="flex-1 truncate text-sm">@{a.username}</span>
                  {a.id === active.id && <Check className="w-4 h-4 text-primary" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          {data?.can_add_another && (
            <DropdownMenuItem
              onClick={openConnect}
              className="cursor-pointer"
              data-testid="sidebar-ig-add-account"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add another account
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={openManage}
            className="cursor-pointer"
            data-testid="sidebar-ig-manage"
          >
            <Settings className="w-4 h-4 mr-2" />
            Manage Instagram
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="cursor-pointer text-destructive focus:text-destructive"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AnimatePresence>
        {switching && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-[10px] text-muted-foreground pt-1.5 text-center"
          >
            Switching account…
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
